import { IPlanInfo } from "components/presentational/views/PlanSubscription";
import { delay } from "application/utils";

// Use to create delay between calls
const TRACKING_CALL_DELAY_MS = 200;
// sequence calls
const first = (runFirst) => {
    runFirst();
    return delay(TRACKING_CALL_DELAY_MS);
}

/* Wrappers for Segment analytics API */

declare interface IAnalyticsJs {
    identify(userId: string, traits?);
    track(eventName, traits?);
    //connect anonymous user with id identity 
    alias(id);
    page(name?);
    user();
    reset();
}

declare const analytics: IAnalyticsJs;

/** identity and track user information  */
export const trackUserInfo = (id, traits?) => {
    if (analytics) {
        // when current id different then have to alias (link) current->new id first
        if (getCurrentUserId() !== id) {
            first(() => analytics.alias(id)).then(() => {
                analytics.identify(id, traits);
            })
        } else if (traits) {
            // Only add additional traits to current user
            analytics.identify(id, traits);
        }
    } else {
        console.error('Identify was called before analytics is ready');
    }
}

// retrieve current user tracking id
const getCurrentUserId = () => {
    if (analytics.user) {
        const user = analytics.user();
        if (user) {
            return user.id();
        }
    }
    return null;
}

/** user signed in */
export const loggedIn = (userId) => {
    first(() => trackUserInfo(userId)).then(() => {
        analytics.track('Customer Portal - Logged In');
    })
}

/** Call-To-Action Button Clicked */
export const ctaButtonClicked = (buttonText) => {
    analytics.track('CTA Button Clicked', { click_text: buttonText })
}

export const checkout = (step) => {
    analytics.track('Acquisition - Checkout - ' + step);
}

// Reset current user/traits
export const logout = () => {
    analytics.reset();
}

/** user successfully completed sign up form 
 *  1. update user info (include new traits)
 *  2. track event
 */
export const signUpCompleted = (authenticationType, traits: { name, email, id, created_at }) => {
    trackUserInfo(getCurrentUserId(), traits);
    analytics.track('Acquisition - Account Created', { authentication_type: authenticationType });
}

/** user successfully verified email address */
export const emailConfirmed = (email) => {
    analytics.track('Acquisition - Verification Completed', { email });
}

export interface IAcquisitionPlanInfo {
    plan_id,
    subscription_type,
    subscription_length,
    subscription_plan,
    redeemed_code?,
    redeemed_benefits?,
}

export interface IUserPlanTraits {
    subscription_status,
    subscription_expiry_date,
    subscription_type,
    subscription_length,
    subscription_plan,
    revenue,
}
/** user selected subscription plan and triggered 'chargebee' modal to complete subscription payment process. */
export const planSelected = (plan: IPlanInfo, redeemedCode?) => {
    const planInfo: IAcquisitionPlanInfo = getPlanInformation(plan, redeemedCode)
    const data = {
        ...planInfo,
        subscription_amount: plan.totalPrice,
    }
    analytics.track('Acquisition - Subscription Plan Selected', data);
}

/** user successfully completed subscription payment and reached "payment confirmation" page after chargebee payment processing. */
export const planSubscriptionCompleted = (plan: IPlanInfo, redeemedCode?, subscriptionStatus?) => {
    const planInfo = getPlanInformation(plan, redeemedCode);
    const info = {
        ...planInfo,
        subscription_status: subscriptionStatus,
        revenue: plan.totalPrice
    }
    const traits = getUserPlanTraits(plan, subscriptionStatus);
    trackUserInfo(getCurrentUserId(), traits);
    analytics.track('Acquisition - Subscription Purchased', info);
}

/** Track current/visited page */
export const pageVisited = () => {
    analytics.page()
}

/* Build plan/subscription information for tracking events */
function getPlanInformation(plan: IPlanInfo, redeemedCode: any): IAcquisitionPlanInfo {
    //ToDo: map this to back-end when available
    const redeemedBenefits = redeemedCode ? 'backer code' : undefined;
    return {
        plan_id: plan.id,
        subscription_plan: plan.languageType,
        subscription_type: plan.subscriptionType,
        subscription_length: plan.durationMonths,
        redeemed_code: redeemedCode,
        redeemed_benefits: redeemedBenefits
    };
}

/* Build plan/subscription information object for identity traits */
function getUserPlanTraits(plan: IPlanInfo, subscriptionStatus: any): IUserPlanTraits {

    return {
        subscription_plan: plan.languageType,
        subscription_type: plan.subscriptionType,
        subscription_length: plan.durationMonths,
        subscription_status: subscriptionStatus,
        subscription_expiry_date: undefined, //ToDo:Beta2 Add mapping
        revenue: plan.totalPrice,
    };
}



// WEBPACK FOOTER //
// ./src/api/Analytics.ts